import { option } from "fp-ts";
import {
  Action,
  Banner,
  Body,
  Box,
  Button,
  CheckboxField,
  Dialog,
  ErrorBanner,
  FileIcon,
  Loader,
  LocalizedString,
  Space,
  useDialogDimensionLimits,
  useIsMobileDevice,
  useIsTouchScreen,
} from "@dbn/omnichannel-design-system-g4p";
import * as classes from "./FilePDFDialog.css";
import { RuntimeLocaleKey, useFormatMessage } from "../../../intl";
import { TaskEither } from "fp-ts/TaskEither";
import { useRemoteData } from "../../../useRemoteData";
import * as remoteData from "../../../RemoteData";
import {
  constant,
  constFalse,
  constUndefined,
  constVoid,
  pipe,
} from "fp-ts/function";
import { DocumentContentOutput, DocumentMeta } from "../../documentAPI";
import { useMemo, useState } from "react";
import { DocumentReviewDialogChild } from "../DocumentReviewDialog/DocumentReviewDialogChild";
import {
  DocumentType,
  DocumentViewer,
} from "../../DocumentViewer/DocumentViewer";
import { spaceUnit } from "@dbn/omnichannel-design-system-g4p/lib/styleConstants";
import { useOrientation } from "react-use";
import { useDownloadDocumentToDevice } from "../../useDownloadDocument";
import { DownloadIcon } from "@g4p/react-styleguide-library";
import { useAppContext } from "../../../useAppContext";

type Props = {
  title: LocalizedString;
  subtitle?: LocalizedString;
  onDismiss?: () => unknown;
  onConfirm?: {
    label: LocalizedString;
    checkboxLabel?: LocalizedString;
    action: () => unknown;
    ariaLabel?: LocalizedString;
  };
  file: TaskEither<unknown, DocumentContentOutput>;
  downloadAction?: TaskEither<unknown, string>;
  infoBannerContent?: LocalizedString;
  errorBannerContent?: LocalizedString;
  mustReadAllDocuments?: boolean;
  isForeignClient?: boolean;
  onForeignDocumentOpen?: (document: DocumentMeta) => unknown;
  document?: DocumentMeta;
  ariaLabelScrollDown?: LocalizedString;
};

export function FilePDFDialog(props: Props) {
  const formatMessage = useFormatMessage();
  const isBranchExperienceTouchScreen = useIsTouchScreen();
  const isMobile = useIsMobileDevice(false);
  const file = useRemoteData(useMemo(() => props.file, []));
  const { heightLimits } = useDialogDimensionLimits();
  const deviceOrientation = useOrientation();
  const downloadDocument = useDownloadDocumentToDevice();
  const [confirmCheckbox, setConfirmCheckbox] = useState(
    !props.onConfirm?.checkboxLabel
  );
  const {
    apiParameters: { tenant },
  } = useAppContext();
  const { maxHeight } = heightLimits();

  const [hasReadAll, setHasReadAll] = useState<boolean>(
    !props.mustReadAllDocuments
  );

  const isFrameWorkAgreement = (document: DocumentMeta) =>
    pipe(
      document.docType,
      option.fold(constFalse, docType => docType === "FRAMEWORK_AGREEMENT")
    );

  const isProductContract = (document: DocumentMeta) =>
    pipe(
      document.docType,
      option.fold(constFalse, docType => docType === "PRODUCT_CONTRACT_ACCOUNT")
    );

  const isInsuranceMediation = (document: DocumentMeta) =>
    pipe(
      document.docType,
      option.fold(constFalse, docType => docType === "INSURANCE_MEDIATION")
    );

  const WrappedIcon = () => {
    return <DownloadIcon width={14} height={14} />;
  };

  const actions = pipe(
    file,
    remoteData.fold<
      unknown,
      DocumentContentOutput,
      React.ComponentProps<typeof Dialog>["actions"]
    >(constant([]), constant([]), file => {
      const actions: Array<Action> = [];
      if (!isBranchExperienceTouchScreen) {
        actions.push({
          disabled: !hasReadAll,
          variant: "text" as const,
          label: formatMessage("Download.buttonLabel"),
          icon: WrappedIcon,
          action: () => downloadDocument(file),
        });
      }

      if (props.onConfirm) {
        actions.push({
          disabled: !hasReadAll || !confirmCheckbox,
          variant: "primary",
          label: props.onConfirm.label || formatMessage("Confirm.buttonLabel"),
          action: props.onConfirm.action,
          ariaLabel:
            props.onConfirm.ariaLabel || formatMessage("Confirm.buttonLabel"),
        });
      }

      return actions.slice(0, 2) as [] | [Action] | [Action, Action];
    })
  );

  const dialogContent = (
    <Box column grow shrink>
      {props.subtitle && (
        <Box hAlignContent="left" marginBottom={5}>
          <Body size="x-small" weight="regular">
            {props.subtitle}
          </Body>
        </Box>
      )}
      {pipe(
        file,
        remoteData.fold(
          () => (
            <Box
              column
              grow
              shrink
              hAlignContent="center"
              vAlignContent="center"
              className={classes.loadingWrapper}
            >
              <Loader
                ariaLabelMessage={formatMessage(
                  "generic.loading.alt" as RuntimeLocaleKey
                )}
              />
            </Box>
          ),
          () => <ErrorBanner>{formatMessage("GenericError")}</ErrorBanner>,
          file => (
            <Box
              column
              grow
              style={{
                minHeight: isMobile
                  ? 0
                  : maxHeight
                  ? maxHeight - 60 * spaceUnit
                  : "30vh",
              }}
            >
              <DocumentViewer
                name={file.filename}
                type={DocumentType.Pdf}
                data={file.content}
                docSet={pipe(
                  props.document,
                  option.fromNullable,
                  option.chain(doc => doc.docset),
                  option.getOrElseW(constUndefined)
                )}
                onLastPageReach={() => {
                  if (!hasReadAll && props.mustReadAllDocuments) {
                    setHasReadAll(true);
                  }
                }}
                ariaLabelScrollDown={props.ariaLabelScrollDown}
                {...(props.infoBannerContent && { minHeight: "50vh" })}
              />
              {props.infoBannerContent && (
                <>
                  <Space units={spaceUnit} />
                  <Box style={{ maxWidth: isMobile ? "87vw" : "unset" }} column>
                    <Banner
                      type="informative"
                      content={props.infoBannerContent}
                      actions={option.none}
                      onDismiss={option.none}
                      title={option.none}
                    />
                  </Box>
                </>
              )}
              {props.errorBannerContent && (
                <>
                  <Space units={spaceUnit} />
                  <Box style={{ maxWidth: isMobile ? "87vw" : "unset" }} column>
                    <Banner
                      type="warning"
                      content={props.errorBannerContent}
                      actions={option.none}
                      onDismiss={option.none}
                      title={option.none}
                      width="100%"
                    />
                  </Box>
                </>
              )}
              {props.onConfirm?.checkboxLabel && (
                <>
                  <Space units={spaceUnit} />
                  <Box style={{ maxWidth: isMobile ? "87vw" : "unset" }} column>
                    <Box>
                      <Space units={2} />
                      <CheckboxField
                        name="checkbox"
                        label={props.onConfirm.checkboxLabel}
                        onChange={() => setConfirmCheckbox(prev => !prev)}
                        value={confirmCheckbox}
                        issues={option.none}
                      />
                    </Box>
                  </Box>
                </>
              )}
            </Box>
          )
        )
      )}
      {props.isForeignClient &&
        props.document &&
        (isFrameWorkAgreement(props.document) ||
          isProductContract(props.document) ||
          (tenant === "CZ" && isInsuranceMediation(props.document))) && (
          <Box>
            <Space units={4} />
            <Button
              variant="text"
              label={formatMessage(
                "UKonto.ReadDocuments.foreignContractsLabel"
              )}
              icon={FileIcon}
              size="small"
              action={() =>
                props.onForeignDocumentOpen && props.document
                  ? props.onForeignDocumentOpen(props.document)
                  : constVoid
              }
            />
          </Box>
        )}
    </Box>
  );

  return (
    <>
      {isMobile ? (
        <Dialog
          ariaLabelMessageForCloseIcon={formatMessage(
            "Modal.closeButton" as RuntimeLocaleKey
          )}
          variant="document"
          title={props.title}
          actions={actions}
          onDismiss={
            props.onDismiss ? option.some(props.onDismiss) : option.none
          }
          mobileModalHeight={"97dvh"}
        >
          <Box
            style={{
              minHeight: deviceOrientation.type.includes("landscape")
                ? "50vh"
                : undefined,
            }}
            grow
            shrink
          >
            {dialogContent}
          </Box>
        </Dialog>
      ) : (
        <Dialog
          ariaLabelMessageForCloseIcon={formatMessage(
            "Modal.closeButton" as RuntimeLocaleKey
          )}
          variant="left"
          size="medium"
          title={props.title}
          actions={actions}
          minHeight={maxHeight ? maxHeight - 60 * spaceUnit : "30vh"}
          onDismiss={
            props.onDismiss ? option.some(props.onDismiss) : option.none
          }
        >
          {dialogContent}
        </Dialog>
      )}
      {isBranchExperienceTouchScreen && <DocumentReviewDialogChild />}
    </>
  );
}
